"use client";

import { env } from "@repo/env";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect } from "react";

import { trackMeta } from "@/lib/meta-pixel";

const PIXEL_ID = env.NEXT_PUBLIC_META_PIXEL_ID;

// Mounted in (marketing) and (platform)/join layouts; lives outside the
// (marketing)/_components folder because that path is conventionally
// private to its route group, and join sits in a sibling group. PageView
// fires on every client-side navigation; route-specific events
// (ViewContent, Lead, CompleteRegistration) are dispatched by their
// respective components via trackMeta(). Pixel no-ops entirely when
// NEXT_PUBLIC_META_PIXEL_ID is unset so local dev and preview deployments
// don't ping Meta.
export function MetaPixel() {
  const pathname = usePathname();

  useEffect(() => {
    if (!PIXEL_ID) {
      return;
    }
    trackMeta({ event: "PageView" });
  }, [pathname]);

  if (!PIXEL_ID) {
    return null;
  }

  return (
    <>
      <Script
        id="meta-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          // Official Meta Pixel base code. PageView is intentionally omitted
          // here; the useEffect above fires the initial PageView with a
          // UUID eventID so CAPI can dedupe.
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${PIXEL_ID}');
          `,
        }}
      />
      <noscript>
        {/* Fallback for JS-disabled clients. eventID is intentionally
            omitted; CAPI dedup needs a UUID that the corresponding server
            event also knows, and a per-render UUID can never match. JS-off
            clients also can't fire form/onboarding events, so there's no
            CAPI pair to dedup with anyway. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}
